import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api/apiClient';
import { ItemListProduct } from '../types/post.type';
import usePagination from './usePagination';

export interface SearchResult {
  data: ItemListProduct[];
  is_next: boolean;
  total: number;
  message: string;
  status: number;
}

export interface ParamSearchInterface {
  paged?: number;
  q?: string;
  sortby?: 'views__desc' | 'like_count__desc' | 'created_at__desc' | 'views_day__desc' | 'views_week__desc' | 'rating_total__desc' | string;
  filter?: string;
}

interface UseSearchProps {
  initialQuery?: string;
  initialFilter?: string;
  initialSort?: string;
  initialPage?: number;
  staleTime?: number;
  cacheTime?: number;
}
export const fetchSearchApi = async (params?: ParamSearchInterface): Promise<SearchResult> => {
  try {
    // Construct URL with paged in the path: /posts/search/paged/{number}
    const url = params?.paged && params?.paged > 1 ? `/posts/search/paged/${params.paged}/` : `/posts/search/`;
    
    console.log('Fetching search with URL:', url, 'and params:', params);
    
    const response = await apiClient.get(url, {
      params: {
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.filter ? { filter: params.filter } : {}),
        ...(params?.sortby ? { sortby: params.sortby } : {}),
      },
    });
    
    console.log('Search API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Search API error:', error);
    // Return a empty result structure to prevent UI errors
    return {
      data: [],
      is_next: false,
      total: 0,
      message: error instanceof Error ? error.message : 'Search failed',
      status: 500
    };
  }
};
const useSearch = ({
  initialQuery = '',
  initialFilter = '',
  initialSort = '',
  initialPage = 1,
  staleTime = 5 * 60 * 1000, // 5 minutes cache
  cacheTime = 10 * 60 * 1000, // 10 minutes
}: UseSearchProps) => {
  const queryClient = useQueryClient();
  const [inputQuery, setInputQuery] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [filter, setFilter] = useState(initialFilter);
  const [sort, setSort] = useState(initialSort);
  const [results, setResults] = useState<ItemListProduct[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  // Pagination hook
  const {
    page,
    setPage,
    hasNextPage,
    setHasNextPage,
    fetchNextPage,
    resetPage,
  } = usePagination({ initialPage });

  // Apply debounce to search input
  useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => {
      setQuery(inputQuery);
      setIsTyping(false);
      // Always reset to page 1 when search query changes
      resetPage();
    }, 500); // 500ms delay
    
    return () => clearTimeout(timer);
  }, [inputQuery, resetPage]);
  
  // Reset page when filter or sort changes
  useEffect(() => {
    if (filter || sort) {
      resetPage();
      // Reset results to avoid mixing results from different filters/sorts
      setResults([]);
    }
  }, [filter, sort, resetPage]);

  // Update UI to show typing state
  const isLoading = isTyping || false;

  // Memoize search parameters to prevent unnecessary re-renders
  const params: ParamSearchInterface = useCallback(() => {
    const searchParams: ParamSearchInterface = {
      paged: page,
    };
    
    if (query) {
      searchParams.q = query;
    }
    
    if (filter) {
      searchParams.filter = `posttype__${filter}`;
    }
    
    if (sort) {
      searchParams.sortby = sort;
    }
    
    return searchParams;
  }, [page, query, filter, sort])();

  // Enhanced search query with proper caching and error handling
  const { data, isLoading: isQueryLoading, refetch, isError } = useQuery({
    queryKey: ['search', params],
    queryFn: () => fetchSearchApi(params),
    staleTime: staleTime,
    gcTime: cacheTime,
    enabled: (!!query || !!filter || !!sort) && page > 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Handle search results with proper pagination
  useEffect(() => {
    if (data) {
      // For page 1, replace results. For other pages, append
      if (page === 1) {
        setResults(data.data || []);
      } else {
        setResults((prevResults) => [...prevResults, ...(data.data || [])]);
      }
      
      setHasNextPage(data.is_next || false);
      setTotalResults(data.total || 0);
      
      // Reset error state on successful request
      setError(null);
    }
  }, [data, page, setHasNextPage]);

  // Handle API errors
  useEffect(() => {
    if (isError) {
      setError('Failed to load search results. Please try again.');
    }
  }, [isError]);

  // Reset all search state
  const resetSearch = useCallback(() => {
    setInputQuery('');
    setQuery('');
    setFilter('');
    setSort('');
    resetPage();
    setResults([]);
    setTotalResults(0);
    setError(null);
    
    // Clear cache for previous search results
    queryClient.invalidateQueries({queryKey: ['search']});
  }, [queryClient, resetPage]);

  return {
    query: inputQuery, // Return the input query for UI binding
    setQuery: setInputQuery, // Use the setter for direct input binding
    filter,
    setFilter,
    sort,
    setSort,
    page,
    setPage,
    results,
    isLoading: isLoading || isQueryLoading, // Combine loading states
    resetSearch,
    refetch,
    fetchNextPage,
    hasNextPage,
    totalResults,
    error,
    isError,
    isTyping, // Expose typing state
  };
};

export default useSearch;
